import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Download, FileSpreadsheet, Loader2 } from "lucide-react";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface ReportExportProps {
  reportName: string;
  reportData: any;
  onSave?: () => void;
}

export function ReportExport({ reportName, reportData, onSave }: ReportExportProps) {
  const [isExporting, setIsExporting] = useState(false);

  const exportToExcel = () => {
    setIsExporting(true);
    try {
      // Convert the report data to a format suitable for Excel
      const worksheet = XLSX.utils.json_to_sheet(prepareDataForExport(reportData));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, reportName);
      
      // Generate a filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const filename = `${reportName.replace(/\s+/g, '_')}_${timestamp}.xlsx`;
      
      // Save the file
      XLSX.writeFile(workbook, filename);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const exportToPdf = () => {
    setIsExporting(true);
    try {
      // Create a new PDF document
      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(18);
      doc.text(reportName, 14, 22);
      
      // Add timestamp
      doc.setFontSize(10);
      const timestamp = new Date().toISOString().slice(0, 10);
      doc.text(`Generated on: ${timestamp}`, 14, 30);
      
      // Add report data as table
      autoTable(doc, {
        startY: 40,
        head: [getReportHeaders(reportData)],
        body: getReportRows(reportData),
      });
      
      // Generate a filename with timestamp
      const filename = `${reportName.replace(/\s+/g, '_')}_${timestamp}.pdf`;
      
      // Save the file
      doc.save(filename);
    } catch (error) {
      console.error("Error exporting to PDF:", error);
    } finally {
      setIsExporting(false);
    }
  };

  // Helper function to prepare data for export based on report type
  const prepareDataForExport = (data: any) => {
    // This function would transform the report data into a simple table format
    // The actual implementation would depend on the structure of your report data
    
    if (Array.isArray(data)) {
      return data;
    }
    
    // Handle financial reports
    if (data.revenue && data.expenses) {
      const exportData = [
        { Category: 'INCOME STATEMENT', Amount: '' },
        { Category: 'Period', Amount: `${formatDate(data.periodStart)} to ${formatDate(data.periodEnd)}` },
        { Category: '', Amount: '' },
        { Category: 'REVENUE', Amount: '' }
      ];
      
      // Add revenue categories
      Object.entries(data.revenue.categories || {}).forEach(([category, amount]) => {
        exportData.push({ Category: category, Amount: formatAmount(amount) });
      });
      
      exportData.push(
        { Category: 'Total Revenue', Amount: formatAmount(data.revenue.total) },
        { Category: '', Amount: '' },
        { Category: 'EXPENSES', Amount: '' }
      );
      
      // Add expense categories
      Object.entries(data.expenses.categories || {}).forEach(([category, amount]) => {
        exportData.push({ Category: category, Amount: formatAmount(amount) });
      });
      
      exportData.push(
        { Category: 'Total Expenses', Amount: formatAmount(data.expenses.total) },
        { Category: '', Amount: '' },
        { Category: 'NET INCOME', Amount: formatAmount(data.netIncome) }
      );
      
      return exportData;
    }
    
    // Handle balance sheet
    if (data.assets && data.liabilities) {
      const exportData = [
        { Category: 'BALANCE SHEET', Amount: '' },
        { Category: 'As of', Amount: formatDate(data.asOfDate) },
        { Category: '', Amount: '' },
        { Category: 'ASSETS', Amount: '' }
      ];
      
      // Add asset accounts
      data.assets.accounts.forEach((account: any) => {
        exportData.push({ Category: account.name, Amount: formatAmount(account.balance) });
      });
      
      exportData.push(
        { Category: 'Total Assets', Amount: formatAmount(data.assets.total) },
        { Category: '', Amount: '' },
        { Category: 'LIABILITIES', Amount: '' }
      );
      
      // Add liability accounts
      data.liabilities.accounts.forEach((account: any) => {
        exportData.push({ Category: account.name, Amount: formatAmount(account.balance) });
      });
      
      exportData.push(
        { Category: 'Total Liabilities', Amount: formatAmount(data.liabilities.total) },
        { Category: '', Amount: '' },
        { Category: 'EQUITY', Amount: '' }
      );
      
      // Add equity accounts
      data.equity.accounts.forEach((account: any) => {
        exportData.push({ Category: account.name, Amount: formatAmount(account.balance) });
      });
      
      exportData.push(
        { Category: 'Total Equity', Amount: formatAmount(data.equity.total) },
        { Category: '', Amount: '' },
        { Category: 'TOTAL LIABILITIES & EQUITY', Amount: formatAmount(data.totalLiabilitiesAndEquity) }
      );
      
      return exportData;
    }
    
    // For other types of reports or unknown formats, return a simple object
    return [{ Report: reportName, Data: 'See PDF for detailed report' }];
  };

  // Helper function to get headers for PDF tables
  const getReportHeaders = (data: any): string[] => {
    if (Array.isArray(data) && data.length > 0) {
      return Object.keys(data[0]);
    }
    
    // Default headers for financial reports
    if (data.revenue && data.expenses) {
      return ['Category', 'Amount'];
    }
    
    // Default headers for balance sheet
    if (data.assets && data.liabilities) {
      return ['Account', 'Balance'];
    }
    
    // Default headers for loan amortization
    if (data.schedule) {
      return ['Payment #', 'Payment', 'Principal', 'Interest', 'Remaining Balance'];
    }
    
    return ['Item', 'Value'];
  };

  // Helper function to get data rows for PDF tables
  const getReportRows = (data: any): any[][] => {
    if (Array.isArray(data)) {
      return data.map(item => Object.values(item).map(val => 
        val === null || val === undefined ? '' : String(val)
      ));
    }
    
    // Simplified implementation - would need to be customized for each report type
    const rows: any[][] = [];
    
    // Add some basic info based on report type
    if (data.revenue && data.expenses) {
      rows.push(['Revenue', formatAmount(data.revenue.total)]);
      rows.push(['Expenses', formatAmount(data.expenses.total)]);
      rows.push(['Net Income', formatAmount(data.netIncome)]);
    } else if (data.assets && data.liabilities) {
      rows.push(['Total Assets', formatAmount(data.assets.total)]);
      rows.push(['Total Liabilities', formatAmount(data.liabilities.total)]);
      rows.push(['Total Equity', formatAmount(data.equity.total)]);
    } else if (data.schedule) {
      // For loan amortization, include the schedule
      data.schedule.forEach((payment: any) => {
        rows.push([
          payment.paymentNumber,
          formatAmount(payment.payment),
          formatAmount(payment.principalPayment),
          formatAmount(payment.interestPayment),
          formatAmount(payment.remainingBalance)
        ]);
      });
    }
    
    return rows;
  };

  // Helper function for date formatting
  const formatDate = (date: Date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  };

  // Helper function for amount formatting
  const formatAmount = (amount: number | unknown) => {
    if (amount === undefined || amount === null) return 'N/A';
    if (typeof amount !== 'number') return 'Invalid Amount';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  return (
    <div className="flex flex-col sm:flex-row gap-2">
      <Button
        variant="outline"
        onClick={exportToExcel}
        disabled={isExporting}
        className="flex items-center gap-2"
      >
        {isExporting ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <FileSpreadsheet className="w-4 h-4" />
        )}
        Export Excel
      </Button>
      
      <Button
        variant="outline"
        onClick={exportToPdf}
        disabled={isExporting}
        className="flex items-center gap-2"
      >
        {isExporting ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Download className="w-4 h-4" />
        )}
        Export PDF
      </Button>
      
      {onSave && (
        <Button
          onClick={onSave}
          disabled={isExporting}
          className="ml-auto"
        >
          Save Report
        </Button>
      )}
    </div>
  );
} 